import React, { useState } from 'react';
import {
  IAuditoriaTransferPricingResultado,
  IItemTransferPricing,
  ILancamentoTP,
} from '../interfaces/IAuditoriaTransferPricing';

interface Props {
  resultado: IAuditoriaTransferPricingResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value?: string) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('pt-BR');
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO': return 'bi-exclamation-circle-fill';
    case 'ALERTA': return 'bi-exclamation-triangle-fill';
    case 'AVISO': return 'bi-info-circle-fill';
    default: return 'bi-check-circle-fill';
  }
};

const AuditoriaTransferPricingDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [lancamentoExpandido, setLancamentoExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const itens = resultado.itens || [];

  const alternarItem = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
    setLancamentoExpandido(null);
  };

  const alternarLancamento = (chave: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLancamentoExpandido(lancamentoExpandido === chave ? null : chave);
  };

  return (
    <>
      {/* Banner de alerta — vermelho em caso de ERRO CRÍTICO (Épico 10, Seção 5) */}
      <div
        className={`auditoria-alert status-${resultado.status}`}
        style={resultado.status === 'ERRO' ? { borderLeftColor: '#9b1c1c', background: '#fff1f2' } : {}}
      >
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`}
          style={resultado.status === 'ERRO' ? { color: '#9b1c1c' } : {}} />
        <div>
          <strong>
            {resultado.status === 'ERRO'
              ? 'ERRO DE AUDITORIA: Inconformidade Fiscal com Partes Relacionadas'
              : `Transfer Pricing e Intercompany — ${resultado.status}`}
          </strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      {/* KPIs — a Exposição Financeira Total é o indicador principal */}
      <div className='auditoria-kpis'>
        <div
          className={`auditoria-kpi ${resultado.exposicaoFinanceiraTotalGeral > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}
          style={resultado.exposicaoFinanceiraTotalGeral > 0 ? { borderColor: '#9b1c1c' } : {}}
        >
          <div className='auditoria-kpi-label'>Risco Total do Passivo</div>
          <div className='auditoria-kpi-value' style={resultado.exposicaoFinanceiraTotalGeral > 0 ? { color: '#9b1c1c' } : {}}>
            {formatCurrency(resultado.exposicaoFinanceiraTotalGeral)}
          </div>
          <div className='auditoria-kpi-help'>
            Etotal = IRPJ+CSLL (34%) + Multa Qualificada (75%) + Multa Acessória (3%)
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Passivo Tributário Principal</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.passivoTributarioPrincipalTotal)}</div>
          <div className='auditoria-kpi-help'>IRPJ (25%) + CSLL (9%) = 34% sobre o valor das transações.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Exposição a Multas</div>
          <div className='auditoria-kpi-value'>
            {formatCurrency(resultado.multaQualificadaTotal + resultado.multaAcessoriaTotal)}
          </div>
          <div className='auditoria-kpi-help'>
            Multa Qualificada 75% (Art. 44, Lei 9.430/96) + Multa Acessória 3% (Art. 12, Lei 8.218/91).
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Lei 14.596/2023, IN RFB 2.161/2023, Lei 9.430/96, Lei 8.218/91.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Base Legal — Transfer Pricing e Intercompany:</strong> {resultado.fundamentacaoLegal}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Análise por Conta — Transações com Partes Relacionadas</h3>
        <p className='text-muted'>
          Cada conta-gatilho de resultado (3.01.01.06.01 e 3.01.02.04.05) é verificada quanto à
          existência de ajuste na conta 6.01.01.01.01 — a ausência gera <strong>ERRO CRÍTICO</strong> com
          projeção de passivo máximo (base legal conforme o período auditado). Contas patrimoniais de
          mútuo (Ativo/Passivo, partes relacionadas ou não) e indícios de intercompany geram{' '}
          <strong>AVISO</strong>, sem cálculo automático de passivo — exigem revisão manual de
          documentação e aplicabilidade. Clique na conta para ver os lançamentos.
        </p>

        {itens.length === 0 && (
          <div className='text-muted mt-4'>
            Nenhuma conta de partes relacionadas, mútuo ou intercompany identificada na ECD do período.
          </div>
        )}

        {itens.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Empresa / Conta</th>
                  <th>Ajuste TP (6.01.01.01.01)</th>
                  <th className='text-end'>Valor da Transação</th>
                  <th className='text-end'>Passivo Tributário (34%)</th>
                  <th className='text-end'>Total Multas</th>
                  <th className='text-end'>Risco Total</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item: IItemTransferPricing, idx) => {
                  const chave = `tp-${item.empresaId}-${item.contaReferencial}-${idx}`;
                  const aberto = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarItem(chave)}>
                        <td>
                          <div><strong>{item.empresaNome}</strong></div>
                          <div className='text-muted' style={{ fontSize: '12px' }}>
                            {item.contaReferencial} — {item.descricaoConta}
                          </div>
                          <div className='text-muted' style={{ fontSize: '11px' }}>CNPJ: {item.cnpj}</div>
                        </td>
                        <td>
                          {/* COR-213: rótulo vem do backend (RotuloAjusteTP) — "Ajuste TP
                              Presente/Ausente" só quando a sujeição já está CONFIRMADA (conta de
                              resultado); "Aplicabilidade Pendente" (indício patrimonial isolado)
                              usa estilo neutro, não o vermelho de "Ausente"/inconformidade. */}
                          <span
                            className={`auditoria-status ${
                              item.status === 'AVISO' ? 'status-AVISO' : (item.possuiAjusteTP ? 'status-CONCILIADO' : 'status-ERRO')
                            }`}
                            style={{ fontSize: '11px' }}
                          >
                            {item.rotuloAjusteTP || (item.possuiAjusteTP ? 'PRESENTE' : 'AUSENTE')}
                          </span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valorTransacao)}</td>
                        <td className='text-end'>
                          {item.passivoTributarioPrincipal > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{formatCurrency(item.passivoTributarioPrincipal)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td className='text-end'>
                          {item.totalMultas > 0
                            ? <strong style={{ color: '#9b1c1c' }}>{formatCurrency(item.totalMultas)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td className='text-end'>
                          {item.exposicaoFinanceiraTotal > 0
                            ? <strong style={{ color: '#9b1c1c', fontSize: '14px' }}>{formatCurrency(item.exposicaoFinanceiraTotal)}</strong>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${aberto ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {aberto && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc', padding: 0 }}>
                            <div className='py-3 px-4'>
                              <div className='mb-3' style={{ fontSize: '13px' }}>
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>

                              {item.status === 'ERRO' && item.lancamentos.length > 0 && (
                                <>
                                  <div className='mb-2'>
                                    <strong>Memória de Cálculo por Lançamento</strong>
                                    <span className='text-muted ms-2' style={{ fontSize: '12px' }}>
                                      (Pi = Vt × 34%; MQ = Pi × 75%; MA = Vt × 3%; Etotal = Pi + MQ + MA)
                                    </span>
                                  </div>
                                  <div className='table-responsive'>
                                    <table className='auditoria-table' style={{ fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th>Data</th>
                                          <th>Conta Contábil</th>
                                          <th>Histórico</th>
                                          <th className='text-end'>Valor (Vt)</th>
                                          <th className='text-end'>Passivo (34%)</th>
                                          <th className='text-end'>Multa Qualificada</th>
                                          <th className='text-end'>Multa Acessória</th>
                                          <th className='text-end'>Risco Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.lancamentos.map((lanc: ILancamentoTP, li) => {
                                          const chaveLanc = `${chave}-lanc-${li}`;
                                          return (
                                            <tr
                                              key={chaveLanc}
                                              style={{ background: '#fff7f7', cursor: 'pointer' }}
                                              onClick={(e) => alternarLancamento(chaveLanc, e)}
                                            >
                                              <td>{formatDate(lanc.dataLancamento)}</td>
                                              <td>{lanc.contaContabil}</td>
                                              <td style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {lanc.historico || <span className='text-muted'>(sem histórico)</span>}
                                              </td>
                                              <td className='text-end'>{formatCurrency(lanc.valor)}</td>
                                              <td className='text-end' style={{ color: '#9b1c1c' }}>
                                                {formatCurrency(lanc.passivoTributarioPrincipal)}
                                              </td>
                                              <td className='text-end'>{formatCurrency(lanc.multaQualificada)}</td>
                                              <td className='text-end'>{formatCurrency(lanc.multaAcessoria)}</td>
                                              <td className='text-end'>
                                                <strong style={{ color: '#9b1c1c' }}>
                                                  {formatCurrency(lanc.exposicaoFinanceiraTotal)}
                                                </strong>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </>
                              )}

                              {item.status === 'CONCILIADO' && (
                                <div className='text-muted' style={{ fontSize: '13px' }}>
                                  Ajuste de Transfer Pricing registrado na conta 6.01.01.01.01 — conformidade verificada
                                  (base legal conforme o período auditado, ver diagnóstico acima).
                                </div>
                              )}

                              {item.status === 'AVISO' && item.lancamentos.length > 0 && (
                                <>
                                  <div className='mb-2'>
                                    <strong>Lançamentos contábeis únicos com indício de mútuo/intercompany</strong>
                                    <span className='text-muted ms-2' style={{ fontSize: '12px' }}>
                                      (sem cálculo de passivo/multa — indício, requer validação manual)
                                    </span>
                                  </div>
                                  {/* COR-211: cada linha é um lançamento ÚNICO (débito e crédito do
                                      mesmo evento já consolidados em uma só linha) — clique para
                                      expandir e ver as partidas contábeis individuais (conta,
                                      débito/crédito, histórico e contrapartida). */}
                                  <div className='table-responsive'>
                                    <table className='auditoria-table' style={{ fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th>Data</th>
                                          <th>Nº Lançamento</th>
                                          <th>Conta Contábil</th>
                                          <th>Histórico</th>
                                          <th className='text-end'>Valor (único)</th>
                                          <th></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.lancamentos.map((lanc: ILancamentoTP, li) => {
                                          const chaveLanc = `${chave}-aviso-${li}`;
                                          const abertoLanc = lancamentoExpandido === chaveLanc;
                                          return (
                                            <React.Fragment key={chaveLanc}>
                                              <tr
                                                style={{ background: '#fffbeb', cursor: 'pointer' }}
                                                onClick={(e) => alternarLancamento(chaveLanc, e)}
                                              >
                                                <td>{formatDate(lanc.dataLancamento)}</td>
                                                <td>{lanc.numeroLancamento || <span className='text-muted'>—</span>}</td>
                                                <td>{lanc.contaContabil}</td>
                                                <td style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                  {lanc.historico || <span className='text-muted'>(sem histórico)</span>}
                                                </td>
                                                <td className='text-end'>{formatCurrency(lanc.valor)}</td>
                                                <td>
                                                  <span className='auditoria-contador auditoria-contador-secundario' style={{ marginRight: 6 }}>
                                                    {lanc.partidas.length} partida{lanc.partidas.length === 1 ? '' : 's'}
                                                  </span>
                                                  <i className={`bi ${abertoLanc ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
                                                </td>
                                              </tr>
                                              {abertoLanc && (
                                                <tr>
                                                  <td colSpan={6} style={{ background: '#fff', padding: '8px 12px 12px 32px' }}>
                                                    {/* COR-212: rastreabilidade completa — número do lançamento (já na
                                                        linha-pai), débito/crédito, contrapartida, referencial da conta
                                                        e motivo específico do enquadramento por partida. */}
                                                    <div className='text-muted mb-1' style={{ fontSize: '11px' }}>
                                                      Partidas do lançamento {lanc.numeroLancamento || '—'} (débito e crédito da mesma operação — contam uma única vez no valor acima):
                                                    </div>
                                                    <table className='auditoria-table' style={{ fontSize: '11px' }}>
                                                      <thead>
                                                        <tr>
                                                          <th>Conta</th>
                                                          <th>Descrição</th>
                                                          <th>Referencial</th>
                                                          <th>D/C</th>
                                                          <th>Contrapartida</th>
                                                          <th>Histórico</th>
                                                          <th className='text-end'>Valor</th>
                                                          <th>Evidência</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {lanc.partidas.map((partida, pi) => (
                                                          <tr key={`${chaveLanc}-partida-${pi}`}>
                                                            <td>{partida.contaContabil}</td>
                                                            <td>{partida.descricaoConta || <span className='text-muted'>—</span>}</td>
                                                            <td className='text-muted'>{partida.contaReferencial || '—'}</td>
                                                            <td>
                                                              <span className={`auditoria-status ${partida.tipoLancamento === 'D' ? 'status-ALERTA' : 'status-AVISO'}`} style={{ fontSize: '10px' }}>
                                                                {partida.tipoLancamento || '—'}
                                                              </span>
                                                            </td>
                                                            <td>{partida.contrapartida}</td>
                                                            <td>{partida.historico || <span className='text-muted'>(sem histórico)</span>}</td>
                                                            <td className='text-end'>{formatCurrency(partida.valor)}</td>
                                                            <td>
                                                              <span
                                                                className='auditoria-status'
                                                                style={{
                                                                  fontSize: '10px',
                                                                  background: partida.forcaEvidencia === 'Forte' ? '#fde2e2' : partida.forcaEvidencia === 'Média' ? '#ffe7cc' : '#eef1f6',
                                                                  color: partida.forcaEvidencia === 'Forte' ? '#9b1c1c' : partida.forcaEvidencia === 'Média' ? '#9a4d00' : '#4b5568',
                                                                }}
                                                                title={partida.motivoEnquadramento}
                                                              >
                                                                {partida.forcaEvidencia || '—'}
                                                              </span>
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                    {lanc.partidas.map((partida, pi) => (
                                                      <div key={`${chaveLanc}-motivo-${pi}`} className='text-muted mt-1' style={{ fontSize: '11px' }}>
                                                        <strong>{partida.contaContabil}:</strong> {partida.motivoEnquadramento}
                                                      </div>
                                                    ))}
                                                  </td>
                                                </tr>
                                              )}
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  {item.memoriaCalculo && (
                                    <div className='text-muted mt-2' style={{ fontSize: '11px' }}>
                                      <strong>Memória de Cálculo:</strong> {item.memoriaCalculo}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaTransferPricingDetalhe;
